import { Component, DOCUMENT, Inject, PLATFORM_ID } from '@angular/core';
import { NavbarControlComponent } from '../shared/controls/navbar-control/navbar-control.component';
import { Router, RouterLink } from '@angular/router';
import { FooterComponent } from '../shared/controls/footer/footer.component';
import { Meta, Title } from '@angular/platform-browser';
import { SharedUserService } from '../shared/services/shared/user/shared-user.service';
import { HttpClient } from '@angular/common/http';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { loadComponentTranslations } from '../shared/funcs/lang-loader';
import { isPlatformBrowser } from '@angular/common';

@Component({
  selector: 'app-features',
  imports: [
    NavbarControlComponent,
    RouterLink,
    FooterComponent,
    TranslateModule,
  ],
  templateUrl: './features.component.html',
  styleUrl: './features.component.scss',
})
export class FeaturesComponent {
  public featureSections = [
    {
      badge: 'BOOKING MANAGEMENT',
      heading: 'Turn visitors into confirmed appointments',
      title: 'Booking Management',
      icon: 'fa-calendar-check',
      description:
        'Create seamless booking experiences while maintaining complete control over scheduling rules.',
      sections: [
        'Appointment Types',
        'Online Booking Pages',
        'Custom Booking Forms',
        'Group Appointments',
        'Multi-location Booking',
        'Booking Rules',
        'Booking Confirmations',
        'Buffer Times',
      ],
    },
    {
      badge: 'AVAILABILITY MANAGEMENT',
      heading: 'Stay in complete control of your calendar',
      title: 'Availability Management',
      icon: 'fa-clock',
      description:
        'Define when customers can book and prevent scheduling conflicts.',
      sections: [
        'Working Hours',
        'Break Times',
        'Holiday Management',
        'Block Off Time',
        'Buffer Rules',
        'Timezone Support',
        'Scheduling Windows',
        'Date Overrides',
      ],
    },
    {
      badge: 'TEAM SCHEDULING',
      heading: 'Coordinate your entire team effortlessly',
      title: 'Team Scheduling',
      icon: 'fa-users',
      description:
        'Coordinate staff schedules, availability, and appointments from one place.',
      sections: [
        'Staff Management',
        'Shared Calendars',
        'Role Permissions',
        'Contributors',
        'Appointment Assignment',
        'Staff Availability',
        'Team Notifications',
        'Location Assignment',
      ],
    },
    {
      badge: 'CLIENT MANAGEMENT',
      heading: 'Build stronger customer relationships',
      title: 'Client Management',
      icon: 'fa-user-friends',
      description:
        'Maintain complete customer profiles and appointment history.',
      sections: [
        'Client Profiles',
        'Contact Information',
        'Appointment History',
        'Client Notes',
        'Custom Fields',
        'Tags',
        'Search & Filters',
        'Activity Tracking',
      ],
    },
    {
      badge: 'NOTIFICATIONS & REMINDERS',
      heading: 'Keep clients informed automatically',
      title: 'Notifications & Reminders',
      icon: 'fa-bell',
      description: 'Reduce no-shows with automated communication.',
      sections: [
        'Email Notifications',
        'SMS Notifications',
        'WhatsApp Notifications',
        'Booking Confirmations',
        'Reminder Messages',
        'Cancellation Alerts',
        'Reschedule Alerts',
        'Custom Templates',
      ],
    },
    {
      badge: 'PAYMENTS',
      heading: 'Get paid before the appointment starts',
      title: 'Payments',
      icon: 'fa-credit-card',
      description: 'Accept and manage payments directly from bookings.',
      sections: [
        'Razorpay Integration',
        'Online Payments',
        'Deposits',
        'Full Payments',
        'Refund Tracking',
        'Payment Status',
        'Transaction History',
        'Payment Reports',
      ],
    },
    {
      badge: 'RESOURCE MANAGEMENT',
      heading: 'Manage rooms, equipment, and shared resources',
      title: 'Resource Management',
      icon: 'fa-cubes',
      description:
        'Manage rooms, equipment, and shared resources alongside appointments.',
      sections: [
        'Resource Booking',
        'Meeting Rooms',
        'Equipment Scheduling',
        'Resource Allocation',
        'Availability Tracking',
        'Conflict Prevention',
        'Resource Calendars',
        'Utilization Reports',
      ],
    },
    {
      badge: 'INVENTORY MANAGEMENT',
      heading: 'Keep inventory aligned with your services',
      title: 'Inventory Management',
      icon: 'fa-boxes',
      description:
        'Track inventory consumption and stock levels connected to services.',
      sections: [
        'Inventory Catalog',
        'Stock Quantities',
        'Low Stock Alerts',
        'Inventory Categories',
        'Inventory Transactions',
        'Stock Adjustments',
        'Usage Tracking',
        'Inventory Reports',
      ],
    },
    {
      badge: 'AUTOMATION',
      heading: 'Let ClockFlex handle repetitive tasks',
      title: 'Automation',
      icon: 'fa-robot',
      description: 'Automate repetitive scheduling and operational tasks.',
      sections: [
        'Auto Confirmations',
        'Auto Reminders',
        'Follow-up Workflows',
        'No-show Tracking',
        'Status Automation',
        'Workflow Rules',
        'Scheduled Actions',
        'Event Triggers',
      ],
    },
    {
      badge: 'REPORTING & ANALYTICS',
      heading: 'Make smarter decisions with real-time insights',
      title: 'Reporting & Analytics',
      icon: 'fa-chart-line',
      description: 'Monitor business performance with real-time insights.',
      sections: [
        'Appointment Reports',
        'Revenue Reports',
        'Client Reports',
        'Staff Performance',
        'Booking Trends',
        'Utilization Reports',
        'Export Data',
        'Activity Logs',
      ],
    },
    {
      badge: 'CUSTOMIZATION',
      heading: 'Make ClockFlex reflect your brand',
      title: 'Customization',
      icon: 'fa-palette',
      description:
        'Adapt ClockFlex to match your business identity and workflow.',
      sections: [
        'Logo Upload',
        'Brand Colors',
        'Custom Booking Pages',
        'Multi-language Support',
        'Custom Messages',
        'Booking Policies',
        'Business Settings',
        'Timezone Settings',
      ],
    },
    {
      badge: 'SECURITY & COMPLIANCE',
      heading: 'Protect your business and customer data',
      title: 'Security & Compliance',
      icon: 'fa-shield-alt',
      description: 'Keep business and customer data protected.',
      sections: [
        'Secure Authentication',
        'Role Permissions',
        'Data Encryption',
        'Audit Logs',
        'Access Controls',
        'Backups',
        'Privacy Controls',
        'Account Security',
      ],
    },
  ];

  public isLoggedIn: boolean = false;

  public constructor(
    private titleService: Title,
    private _router: Router,
    private metaService: Meta,
    @Inject(DOCUMENT) private document: Document,
    private _sharedUserService: SharedUserService,
    private _translateService: TranslateService,

    private _http: HttpClient,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {}

  public ngOnInit(): void {
    const desc =
      'Explore ClockFlex features including online booking, appointment scheduling, team management, client management, payments, automation, resource management, inventory tracking, reporting, and more.';

    this.titleService.setTitle(
      'ClockFlex Features | Scheduling, Booking & Business Management Tools',
    );

    // ✅ Canonical
    const canonicalUrl = `https://clockflex.com${this._router.url.split('?')[0]}`;
    this.setCanonicalUrl(canonicalUrl);

    // ✅ Meta Description
    this.metaService.updateTag({
      name: 'description',
      content: desc,
    });

    // ✅ Open Graph
    this.metaService.updateTag({
      property: 'og:title',
      content:
        'ClockFlex Features | Scheduling, Booking & Business Management Tools',
    });

    this.metaService.updateTag({
      property: 'og:description',
      content: desc,
    });

    this.metaService.updateTag({
      property: 'og:url',
      content: canonicalUrl,
    });

    this.metaService.updateTag({
      property: 'og:type',
      content: 'website',
    });

    this.metaService.updateTag({
      property: 'og:site_name',
      content: 'ClockFlex',
    });

    // ✅ Twitter
    this.metaService.updateTag({
      name: 'twitter:card',
      content: 'summary_large_image',
    });

    this.metaService.updateTag({
      name: 'twitter:title',
      content:
        'ClockFlex Features | Scheduling, Booking & Business Management Tools',
    });

    this.metaService.updateTag({
      name: 'twitter:description',
      content: desc,
    });

    this.metaService.updateTag({
      name: 'twitter:image',
      content: 'https://clockflex.com/assets/images/og-image.png',
    });
    let lang = this._translateService.currentLang || 'en';

    this._http
      .get<any>('./assets/i18n/static-content/detailed-features.json')
      .subscribe({
        next: (res) => {
          this.featureSections = res[lang]?.['features'];
        },
      });

    loadComponentTranslations(
      this._http,
      this._translateService,
      'components/features',
      this.isBrowser(),
    ).subscribe({
      next: () => {},
      error: () => {},
    });
    this.subscribeUserService();
  }

  private isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  private setCanonicalUrl(url: string): void {
    if (!url.startsWith('http')) {
      return;
    }

    let link: HTMLLinkElement | null = this.document.querySelector(
      "link[rel='canonical']",
    );

    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.document.head.appendChild(link);
    }

    link.setAttribute('href', url);
  }

  private subscribeUserService(): void {
    this._sharedUserService.loggedIn$.subscribe((response) => {
      this.isLoggedIn = response ? true : false;
    });
  }
}
